import { useState, useEffect } from "react";
import { X, MessageCircle, Bell, Zap, ExternalLink, Loader2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * TelegramInviteBanner - Shows once per session for users not connected to Telegram
 * 
 * Props:
 * - token: Auth token for the user
 * - userType: "hospital" | "contractor" | "supplier"
 * - userName: Display name for personalization
 */
export default function TelegramInviteBanner({ token, userType = "hospital", userName = "" }) {
  const [visible, setVisible] = useState(false);
  const [isLinked, setIsLinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [telegramLink, setTelegramLink] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  const SESSION_KEY = `telegram_banner_dismissed_${userType}`;

  useEffect(() => {
    checkTelegramStatus();
  }, [token]);

  const checkTelegramStatus = async () => {
    try {
      const res = await fetch(`${API}/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setIsLinked(data.linked);
        
        // Only show banner if not linked and not dismissed this session
        if (!data.linked && !sessionStorage.getItem(SESSION_KEY)) {
          setVisible(true);
        }
      }
    } catch (err) {
      console.error("Failed to check Telegram status:", err);
    } finally {
      setLoading(false);
    }
  };

  const dismissBanner = () => {
    sessionStorage.setItem(SESSION_KEY, "true");
    setVisible(false);
  };

  const generateAndOpenLink = async () => {
    setGenerating(true);
    try {
      let endpoint;
      if (userType === "contractor") {
        endpoint = `${API}/telegram/contractor/generate-link`;
      } else if (userType === "supplier") {
        endpoint = `${API}/telegram/supplier/generate-link`;
      } else if (userType === "hospital") {
        endpoint = `${API}/telegram/hospital/generate-link`;
      } else if (userType === "admin") {
        endpoint = `${API}/telegram/admin/generate-link`;
      } else {
        endpoint = `${API}/telegram/generate-link`;
      }
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ user_type: userType })
      });
      
      if (res.ok) {
        const data = await res.json();
        setTelegramLink(data.telegram_link);
        window.open(data.telegram_link, "_blank");
        toast.success("Telegram opened! Click 'Start' to connect.");
        
        // Start polling to check if linked
        const pollInterval = setInterval(async () => {
          const statusRes = await fetch(`${API}/telegram/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.linked) {
              setIsLinked(true);
              setVisible(false);
              clearInterval(pollInterval);
              toast.success("Telegram connected successfully! You'll now receive instant notifications.");
            }
          }
        }, 3000);
        
        // Stop polling after 2 minutes
        setTimeout(() => clearInterval(pollInterval), 120000);
      } else {
        toast.error("Failed to generate connection link");
      }
    } catch (err) {
      toast.error("Failed to connect to Telegram");
    } finally {
      setGenerating(false);
    }
  };

  // Don't render if loading, already linked, or dismissed
  if (loading || isLinked || !visible) {
    return null;
  }

  // User type specific benefits
  const benefits = {
    hospital: [
      "Get instant alerts when contractors accept jobs",
      "Receive status updates on your maintenance requests",
      "Submit requests directly from Telegram"
    ],
    contractor: [
      "Get notified instantly when new jobs are assigned",
      "Accept or decline jobs with one tap",
      "Receive job completion reminders"
    ],
    supplier: [
      "Bid instantly! Reply QUOTE [price] [days] to any alert",
      "Get notified first when hospitals post new requests",
      "Never miss a deal — win from your pocket"
    ],
    admin: [
      "Create proxy backorders & maintenance tickets",
      "Monitor system-wide pipeline health",
      "Receive alerts when contractors arrive on-site"
    ]
  };

  const userBenefits = benefits[userType] || benefits.hospital;

  return (
    <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 rounded-xl p-5 mb-6 shadow-lg overflow-hidden" data-testid="telegram-invite-banner">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
      </div>
      
      {/* Dismiss button */}
      <button 
        onClick={dismissBanner}
        className="absolute top-3 right-3 p-1 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        title="Remind me later"
        data-testid="dismiss-telegram-banner"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative flex flex-col md:flex-row md:items-center gap-4">
        {/* Icon and Title */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-7 h-7 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">
              {userName ? `${userName}, get ` : "Get "}instant notifications via Telegram!
            </h3>
            <p className="text-blue-100 text-sm mt-0.5">
              Never miss important updates — connect in seconds
            </p>
          </div>
        </div>

        {/* Benefits - Hidden on mobile for space */}
        <div className="hidden lg:flex items-center gap-6 flex-1 justify-center">
          {userBenefits.slice(0, 2).map((benefit, idx) => (
            <div key={idx} className="flex items-center gap-2 text-white/90 text-sm">
              <Zap className="w-4 h-4 text-yellow-300 flex-shrink-0" />
              <span>{benefit}</span>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex items-center gap-2 md:ml-auto">
          <Button
            onClick={generateAndOpenLink}
            disabled={generating}
            className="bg-white text-blue-600 hover:bg-blue-50 font-medium shadow-md"
            data-testid="connect-telegram-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect Now
              </>
            )}
          </Button>
          <Button
            onClick={() => setShowHelp(true)}
            variant="ghost"
            className="text-white/80 hover:text-white hover:bg-white/10 hidden sm:flex"
            data-testid="telegram-help-btn"
          >
            <HelpCircle className="w-4 h-4 mr-1" />
            How it works
          </Button>
          <Button
            onClick={dismissBanner}
            variant="ghost"
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="remind-later-btn"
          >
            Later
          </Button>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowHelp(false)}>
          <div className="bg-slate-900 rounded-xl max-w-lg w-full p-6 border border-slate-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-400" />
                How to Connect Telegram
              </h3>
              <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 text-slate-300">
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">1</div>
                <p>Click <strong className="text-white">"Connect Now"</strong> to generate your unique link</p>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">2</div>
                <p>Telegram will open with <strong className="text-white">@askisaac_bot</strong></p>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">3</div>
                <p>Press <strong className="text-white">"Start"</strong> in Telegram to connect your account</p>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">✓</div>
                <p>Done! You'll receive instant notifications on your phone</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-slate-800 rounded-lg">
              <h4 className="font-medium text-white mb-2">
                {userType === "contractor" ? "Your Commands:" : userType === "supplier" ? "Your Commands:" : userType === "admin" ? "Admin Commands:" : "Your Commands:"}
              </h4>
              <div className="text-sm text-slate-400 space-y-1">
                {userType === "contractor" ? (
                  <>
                    <p><code className="text-cyan-400">/jobs</code> - View your active jobs</p>
                    <p><code className="text-cyan-400">/arrived</code> - Mark arrival on-site</p>
                    <p><code className="text-cyan-400">/update</code> - Send work progress</p>
                    <p><code className="text-cyan-400">/parts</code> - Request parts</p>
                    <p><code className="text-cyan-400">/complete</code> - Mark job done</p>
                  </>
                ) : userType === "supplier" ? (
                  <>
                    <p><code className="text-cyan-400">/opportunities</code> - All open backorders</p>
                    <p><code className="text-cyan-400">/quote</code> - Submit a quote</p>
                    <p><code className="text-cyan-400">/myquotes</code> - Track your quotes</p>
                    <p><code className="text-cyan-400">QUOTE [price] [days]</code> - Quick bid!</p>
                  </>
                ) : userType === "admin" ? (
                  <>
                    <p><code className="text-cyan-400">/backorder</code> - Proxy backorder for hospital</p>
                    <p><code className="text-cyan-400">/service</code> - Proxy maintenance ticket</p>
                    <p><code className="text-cyan-400">/cancel</code> - Void a ticket</p>
                    <p><code className="text-cyan-400">/pipeline</code> - System health status</p>
                  </>
                ) : (
                  <>
                    <p><code className="text-cyan-400">/status</code> - View open tickets</p>
                    <p><code className="text-cyan-400">/maintenance</code> - Submit request</p>
                    <p><code className="text-cyan-400">/backorder</code> - Order supplies</p>
                  </>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowHelp(false)}>
                Close
              </Button>
              <Button onClick={() => { setShowHelp(false); generateAndOpenLink(); }}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
